// 能源管理的 API client（plan SPEC #energy-api；app-api WP-6）。走 SDK 的 kinioFetch（401 → refresh、403 REAUTH_REQUIRED → 重新驗證）。
// mock：app-api 還沒有的端點（MISSING_ENDPOINTS）在 dev / test 可以改由 mockData.ts 回（mockMode.ts 的開關，prod 一律關）。
// 回應形狀：types.ts；app-api 上線後若欄位名不同，只改這裡的轉換。
import { KinioError, kinioFetch, kinioFetchText, planeBase } from '@kinio/web-sdk';
import { energyMock, energyMockWorld, type EnergyMockWorld, type MockHomeIn, type MockOrgIn } from './mockData';
import type { EnergyMockMode } from './mockMode';
import {
  deviceOf,
  homeSummaryOf,
  periodsOf,
  rankPageOf,
  rankSortOf,
  reportOf,
  settingsOf,
  summaryOf,
  tariffListOf,
  tariffOf,
  templatesOf,
  type WireOrgSummary,
} from './wire';
import type {
  EnergySettings,
  EnergySummary,
  OrgEnergySettings,
  Period,
  PowerModeResult,
  SeriesPoint,
  Tariff,
  TariffRates,
} from './types';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * app-api 還沒有的端點（mock 模式 `missing` 只接這些）；WP-6 上一個拿掉一個，畫面不用改（形狀差異在 wire.ts 轉）。
 * 2026-10-21：WP-6 的實際形狀在 ~/work/handoff/2026-10-21-resume/energy-wp6-api.md，都還沒上 dev。
 */
export const MISSING_ENDPOINTS: { method: Method; re: RegExp }[] = [
  // WP-6 單位 1–3（platform 分支 energy-wp6，2026-10-21 還沒上 dev）：上 dev 後拿掉這幾條
  { method: 'GET', re: /^\/v1\/orgs\/[^/]+\/energy\/(summary|ranking|reports\/[\d-]+(\.csv)?)$/ },
  { method: 'GET', re: /^\/v1\/homes\/[^/]+\/energy\/(summary|series|settings|export\.csv)$/ },
  { method: 'PUT', re: /^\/v1\/homes\/[^/]+\/energy\/settings$/ },
  { method: 'GET', re: /^\/v1\/homes\/[^/]+\/things\/[^/]+\/energy$/ },
  { method: 'GET', re: /^\/v1\/orgs\/[^/]+\/energy\/tariffs(\/[^/]+)?$/ },
  { method: 'POST', re: /^\/v1\/orgs\/[^/]+\/energy\/tariffs(\/[^/]+\/(versions|apply))?$/ },
  { method: 'PUT', re: /^\/v1\/orgs\/[^/]+\/energy\/tariffs\/[^/]+$/ },
  { method: 'DELETE', re: /^\/v1\/orgs\/[^/]+\/energy\/tariffs\/[^/]+$/ },
  { method: 'GET', re: /^\/v1\/energy\/tariff-templates$/ },
  // WP-6 單位 4（還沒做）：功耗模式批次、組織的碳排設定
  { method: 'POST', re: /^\/v1\/orgs\/[^/]+\/energy\/power-mode$/ },
  { method: 'GET', re: /^\/v1\/orgs\/[^/]+\/energy\/power-mode\/status$/ },
  { method: 'GET', re: /^\/v1\/orgs\/[^/]+\/energy\/settings$/ },
  { method: 'PUT', re: /^\/v1\/orgs\/[^/]+\/energy\/settings$/ },
];

export function isMissingEndpoint(method: Method, path: string): boolean {
  return MISSING_ENDPOINTS.some((e) => e.method === method && e.re.test(path));
}

// ---- 執行環境（hooks.ts useEnergyEnv 每次 render 設） ----

let mode: EnergyMockMode = 'off';
let world: EnergyMockWorld | null = null;
let worldKey = '';

export function configureEnergy(m: EnergyMockMode, homes: MockHomeIn[], orgs: MockOrgIn[]): void {
  mode = m;
  if (m === 'off') return;
  const key = JSON.stringify([homes.map((h) => h.home_id), orgs.map((o) => o.org_id)]);
  if (key !== worldKey || !world) {
    world = energyMockWorld(homes, orgs);
    worldKey = key;
  }
}

/** 測試用：直接換 mock 世界。 */
export function setEnergyMockWorld(w: EnergyMockWorld | null, m: EnergyMockMode = 'all'): void {
  world = w;
  worldKey = w ? 'test' : '';
  mode = w ? m : 'off';
}

export function energyMode(): EnergyMockMode {
  return mode;
}

/** 這個請求會不會走 mock（送給真的 app-api 時要換成平台的參數 / body 形狀）。 */
export function mocked(method: Method, path: string): boolean {
  return shouldMock(method, path.split('?')[0]);
}

function shouldMock(method: Method, path: string): boolean {
  return !!world && (mode === 'all' || (mode === 'missing' && isMissingEndpoint(method, path)));
}

async function call<T>(api: string, method: Method, path: string, body?: unknown): Promise<T> {
  const [p, qs] = path.split('?');
  if (shouldMock(method, p)) {
    // 一點點延遲：畫面的載入骨架看得到
    await new Promise((r) => setTimeout(r, 120));
    const res = energyMock(world!, method, p, new URLSearchParams(qs ?? ''), body, Date.now());
    if (!res) throw new KinioError(404, 'NOT_FOUND', 'mock: ' + p);
    if (res.status >= 400) {
      const b = res.body as { error: string; message: string; detail?: Record<string, unknown> };
      throw new KinioError(res.status, b.error, b.message, b.detail);
    }
    return res.body as T;
  }
  return kinioFetch<T>(`${planeBase(api)}${path}`, { method, body });
}

function notFoundNull(e: unknown): null {
  if (e instanceof KinioError && (e.status === 404 || e.status === 501)) return null;
  throw e;
}

const q = (o: Record<string, string | number | undefined | null>) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
const e = encodeURIComponent;

export interface ScopeArgs {
  org: string;
  scope: string;
  period: Period;
  date?: string;
}

const any = <T,>(x: unknown) => x as T;

export const energyApi = {
  orgSummary: async (api: string, a: ScopeArgs) =>
    ({ org_id: a.org, ...summaryOf(await call<WireOrgSummary>(api, 'GET', `/v1/orgs/${e(a.org)}/energy/summary?${q({ scope: a.scope, period: a.period, date: a.date })}`)) }),
  ranking: async (
    api: string,
    a: ScopeArgs & { kind?: string; group?: 'device' | 'poe_port'; sort?: string; cursor?: string; home?: string; text?: string; limit?: number; gaps?: string },
  ) => {
    const path = `/v1/orgs/${e(a.org)}/energy/ranking`;
    // 平台的排序鍵只有 mwh / max_mw / cost；mock 照畫面的鍵
    const sort = mocked('GET', path) ? a.sort : rankSortOf(a.sort);
    return rankPageOf(
      await call(api, 'GET', `${path}?${q({ scope: a.scope, period: a.period, date: a.date, kind: a.kind, group: a.group, sort, cursor: a.cursor, home: a.home, q: a.text, limit: a.limit, gaps: a.gaps })}`),
    );
  },
  report: async (api: string, org: string, scope: string, month: string) => reportOf(await call(api, 'GET', `/v1/orgs/${e(org)}/energy/reports/${month}?${q({ scope })}`)),
  /** 月報 CSV（kind = homes_daily：逐{leaf} × 逐日；meters_monthly：逐量測點 × 逐月）。mock 或平台還沒有時回 null，畫面自己組。 */
  reportCsv: async (api: string, org: string, scope: string, month: string, kind: 'homes_daily' | 'meters_monthly'): Promise<string | null> => {
    const path = `/v1/orgs/${e(org)}/energy/reports/${month}.csv`;
    if (shouldMock('GET', path)) return null;
    // 平台還沒有 CSV 端點（404）→ 畫面自己組
    return kinioFetchText(`${planeBase(api)}${path}?${q({ scope, kind })}`).catch(notFoundNull);
  },
  homeSummary: async (api: string, home: string, period: Period, date?: string) => homeSummaryOf(home, await call(api, 'GET', `/v1/homes/${e(home)}/energy/summary?${q({ period, date })}`)),
  homeSeries: (api: string, home: string, granularity: 'hour' | 'day' | 'month', from: string, to: string, meters?: string[]) =>
    call<{ points: SeriesPoint[] }>(api, 'GET', `/v1/homes/${e(home)}/energy/series?${q({ granularity, from, to, meters: meters?.join(',') })}`),
  /** 單台：period = day（每小時）/ week / month（每日）；Station 帶回各孔。 */
  device: async (api: string, home: string, device: string, o: { period?: 'day' | 'week' | 'month' } = {}) =>
    deviceOf(home, device, await call(api, 'GET', `/v1/homes/${e(home)}/things/${e(device)}/energy?${q({ period: o.period ?? 'day' })}`)),
  deviceCsv: async (api: string, home: string, device: string, granularity: 'hour' | 'day', from: string, to: string): Promise<string | null> => {
    const path = `/v1/homes/${e(home)}/energy/export.csv`;
    if (shouldMock('GET', path)) return null;
    return kinioFetchText(`${planeBase(api)}${path}?${q({ granularity, from, to, meters: device })}`).catch(notFoundNull);
  },
  settings: async (api: string, home: string) => settingsOf(home, await call(api, 'GET', `/v1/homes/${e(home)}/energy/settings`)),
  putSettings: async (api: string, home: string, body: Partial<EnergySettings> & { version?: number }) => {
    // 平台只收 carbon_category / alerts / tariff_id / effective_from（另有 managed_by_template: null 解除範本管理）
    const { carbon_category, alerts, tariff_id, managed_by_template, version } = body;
    return settingsOf(home, await call(api, 'PUT', `/v1/homes/${e(home)}/energy/settings`, { carbon_category, alerts, tariff_id, managed_by_template, version }));
  },
  orgSettings: (api: string, org: string) => call<OrgEnergySettings>(api, 'GET', `/v1/orgs/${e(org)}/energy/settings`),
  putOrgSettings: (api: string, org: string, body: Partial<OrgEnergySettings>) => call<OrgEnergySettings>(api, 'PUT', `/v1/orgs/${e(org)}/energy/settings`, body),
  tariffs: async (api: string, org: string) => {
    const r = await call<{ items?: Tariff[]; names?: EnergySummary['names'] }>(api, 'GET', `/v1/orgs/${e(org)}/energy/tariffs`);
    return { ...tariffListOf(org, any(r)), names: r?.names };
  },
  createTariff: async (api: string, org: string, body: { name: string; kind: Tariff['kind']; currency: string; template_id?: string; effective_from: string; rates: TariffRates }) => {
    const path = `/v1/orgs/${e(org)}/energy/tariffs`;
    const b = mocked('POST', path) ? body : { name: body.name, kind: body.kind, currency: body.currency, template_id: body.template_id, effective_from: body.effective_from, periods: periodsOf(body.kind, body.rates) };
    return tariffOf(org, await call(api, 'POST', path, b));
  },
  deleteTariff: (api: string, org: string, tariff: string) => call<void>(api, 'DELETE', `/v1/orgs/${e(org)}/energy/tariffs/${e(tariff)}`),
  addVersion: async (api: string, org: string, tariff: string, body: { effective_from: string; rates: TariffRates; kind?: Tariff['kind'] }) => {
    const path = `/v1/orgs/${e(org)}/energy/tariffs/${e(tariff)}/versions`;
    const b = mocked('POST', path) ? body : { effective_from: body.effective_from, kind: body.kind, periods: periodsOf(body.kind ?? 'flat', body.rates) };
    return tariffOf(org, await call(api, 'POST', path, b));
  },
  applyTariff: async (api: string, org: string, tariff: string, body: { home_ids: string[]; effective_from: string }) =>
    tariffOf(org, await call(api, 'POST', `/v1/orgs/${e(org)}/energy/tariffs/${e(tariff)}/apply`, body)),
  templates: async (api: string, lang: string, region?: string) => templatesOf(await call(api, 'GET', `/v1/energy/tariff-templates?${q({ region })}`), lang),
  powerMode: (api: string, org: string, device_refs: string[], m: number) => call<{ results: PowerModeResult[] }>(api, 'POST', `/v1/orgs/${e(org)}/energy/power-mode`, { device_refs, mode: m }),
  powerModeStatus: (api: string, org: string, device_refs: string[]) =>
    call<{ results: PowerModeResult[] }>(api, 'GET', `/v1/orgs/${e(org)}/energy/power-mode/status?${q({ device_refs: device_refs.join(',') })}`),
};
